import { useEffect, useState } from "react";
import { User, UserSchema } from "../models/user";
import { jwtDecode } from "jwt-decode";
import { Workspace, WorkspaceSchema } from "../models/workspace";
import { fetchFastApi, refetchAccessToken } from "../utils/fastApi";

export const useCheckTokenAndFetchUser = () => {
  const [user, setUser] = useState<User | null>(null);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);

  const checkAndRefreshToken = async () => {
    const refreshToken = localStorage.getItem("refresh_token");
    const accessToken = localStorage.getItem("access_token");
    const idToken = localStorage.getItem("id_token");

    if (!refreshToken || !idToken || !accessToken) {
      setIsAuthenticated(false);
      return;
    }

    const decodedAccessToken = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;
    const timeLeft = decodedAccessToken.exp! - currentTime;
    if (timeLeft > 900) {
      setIsAuthenticated(true);
      setAccessToken(accessToken);
      return;
    }

    try {
      const refetchResponse = await refetchAccessToken();
      if (refetchResponse) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }
    } catch (e) {
      console.error(e);
      setIsAuthenticated(false);
    }
  };

  const fetchUser = async () => {
    try {
      const response = await fetchFastApi({ method: "GET", path: "/user/" });
      if (response.user) {
        const parsedUser = UserSchema.parse(response.user);
        setUser(parsedUser);
      } else {
        throw new Error("Failed to fetch user");
      }
      if (response.workspace) {
        const parsedWorkspace = WorkspaceSchema.parse(response.workspace);
        setWorkspace(parsedWorkspace);
        localStorage.setItem("workspace_id", parsedWorkspace.id);
      }
    } catch (e) {
      console.error(e);
      throw new Error("Failed to fetch user");
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("access_token")) {
      setLoading(false);
      return;
    }

    const fetchUserAndCheckToken = async () => {
      setLoading(true);
      try {
        await checkAndRefreshToken();
        await fetchUser();
      } catch (e) {
        console.error(e);
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndCheckToken();
  }, []);

  return { user, workspace, loading, error, isAuthenticated, accessToken };
};
